/*
 * Type-ahead for the search modal.
 *
 * Horizon's own predictive search is dead on this shop: Shopify answers
 * `417 Unsupported buyer locale` for an Arabic storefront, so the dropdown had
 * nothing to draw and the modal fell back to a wall of recently-viewed
 * products. Ordinary search is unaffected, so this reads the same query
 * through templates/search.predict.liquid and renders the dropdown itself.
 *
 * Three behaviours are deliberate:
 *
 * Nothing shows until the shopper types - an empty box is the correct answer
 * to an empty query, and the grid of unrelated products was the complaint that
 * started this.
 *
 * Every match is reachable. The list is not truncated; it pages as the shopper
 * scrolls, because a brand name can match four hundred products and neither
 * hiding them nor loading them all on every keystroke is acceptable.
 *
 * A query with no hits is never a dead end: the longest word is tried on its
 * own, because a shopper who mistypes a model name usually gets the other word
 * right, and the categories are offered underneath either way.
 */
(() => {
  const MIN = 2;
  const WAIT = 220;
  const NEAR_END = 240;
  const MAX_AUTOFILL = 3;
  const MAX_COLLECTIONS = 3;

  const CATEGORIES = [
    ['بانيو', 'بانيو'],
    ['أحواض الحمام', 'حوض-حمام'],
    ['خلاطات الحوض', 'خلاط-حوض'],
    ['مجموعات الدش', 'مجموعة-دش'],
    ['القواعد', 'قاعدة-ارضي'],
    ['إكسسوارات', 'اكسسوارات-حمام'],
  ];

  // Shoppers type ا for أ, ه for ة and drop the hamza; none of that should
  // cost them a match.
  const norm = (s) =>
    String(s)
      .replace(/[ً-ْـ]/g, '')
      .replace(/[أإآٱ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/[ىئ]/g, 'ي')
      .replace(/ؤ/g, 'و')
      .toLowerCase();


  /*
   * Egyptian words for the things the catalogue names differently.
   *
   * Shopify's own synonym feature is switched off - the Search & Discovery app
   * says creation of synonym groups is "temporarily paused" - and what it adds
   * automatically is not Egyptian Arabic. So the mapping lives here. A shopper
   * says حنفية; every tap in the catalogue is called خلاط, and searching the
   * word they used returned sixteen products by coincidence, because one
   * supplier is named عبده حنفية.
   *
   * Keys are written already normalised (ة as ه, no hamza) because that is the
   * form the query is compared in. Values are the catalogue's own words and go
   * to Shopify as typed: its search matches any word of a query, which is a
   * problem everywhere else and exactly what is wanted here.
   */
  const SYNONYMS = [
    { keys: ['حنفيه', 'صنبور', 'حنفيات'], vals: ['خلاط'] },
    { keys: ['مغسله', 'لافابو', 'مغاسل'], vals: ['حوض حمام'] },
    { keys: ['كرسي حمام', 'تواليت', 'مرحاض', 'سيفون'], vals: ['قاعدة', 'خزان'] },
    { keys: ['سينك'], vals: ['حوض مطبخ'] },
    { keys: ['دولاب حمام', 'وحده مغسله', 'دولاب'], vals: ['وحدة حمام'] },
    { keys: ['حوض استحمام', 'بانيوهات'], vals: ['بانيو'] },
    { keys: ['شاور'], vals: ['دش'] },
    { keys: ['دش مطر', 'راس دش', 'شاور هيد'], vals: ['طاسة دش'] },
    { keys: ['صندوق طرد'], vals: ['خزان'] },
    { keys: ['هيتر'], vals: ['سخان'] },
    { keys: ['بيديه'], vals: ['شطاف'] },
  ];

  // Widen the query with the catalogue's own word for what the shopper said.
  function expandQuery(q) {
    const nq = norm(q);
    const add = new Set();
    for (const entry of SYNONYMS) {
      for (const key of entry.keys) {
        if (nq.includes(key)) entry.vals.forEach((v) => add.add(v));
      }
    }
    return add.size ? q + ' ' + [...add].join(' ') : q;
  }

  // Alternatives per typed word, so narrowing does not throw away the rows the
  // synonym just went and found.
  function alternatives(word) {
    const alts = new Set([word]);
    for (const entry of SYNONYMS) {
      if (entry.keys.some((k) => k === word || k.split(' ').includes(word))) {
        entry.vals.forEach((v) => alts.add(norm(v)));
      }
    }
    return [...alts];
  }

  // Shopify matches any word of the query, so a second word widens the results
  // instead of narrowing them. Keep only rows carrying every word, which is
  // what a shopper means by typing more.
  function narrow(items, q) {
    const words = norm(q).split(/\s+/).filter((w) => w.length >= 2);
    if (words.length < 2) return items;
    const groups = words.map(alternatives);
    const kept = items.filter((it) => {
      const hay = norm((it.t || '') + ' ' + (it.v || ''));
      return groups.every((alts) => alts.some((a) => hay.includes(a)));
    });
    return kept.length ? kept : items;
  }

  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  async function query(q, page) {
    const url =
      '/search?type=product&view=predict&options%5Bprefix%5D=last&q=' +
      encodeURIComponent(q) +
      (page > 1 ? '&page=' + page : '');
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(res.status);
    return res.json();
  }

  function rowHTML(item) {
    const img = item.i
      ? `<img src="${esc(item.i)}" alt="" loading="lazy" width="56" height="56">`
      : '<span class="es-predict__noimg"></span>';
    const was = item.c ? `<s>${esc(item.c)}</s>` : '';
    return `<a class="es-predict__row" href="${esc(item.u)}">
      <span class="es-predict__thumb">${img}</span>
      <span class="es-predict__text">
        <span class="es-predict__title">${esc(item.t)}</span>
        ${item.v ? `<span class="es-predict__vendor">${esc(item.v)}</span>` : ''}
      </span>
      <span class="es-predict__price">${esc(item.p)}${was}</span>
    </a>`;
  }

  // Shopify's search never returns collections, so a shopper typing a brand
  // would be shown its taps one by one and never told the brand has a section.
  // The section is the better answer to that query, so it goes first.
  function collectionsFor(cols, q) {
    if (!cols || !cols.length) return [];
    const words = norm(q).split(/\s+/).filter((w) => w.length >= 2);
    if (!words.length) return [];
    return cols
      .filter((c) => {
        const hay = norm(c.t);
        return words.map(alternatives).every((alts) => alts.some((a) => hay.includes(a)));
      })
      .sort((a, b) => b.n - a.n)
      .slice(0, MAX_COLLECTIONS);
  }

  function collectionHTML(c) {
    const img = c.i
      ? `<img src="${esc(c.i)}" alt="" loading="lazy" width="56" height="56">`
      : '<span class="es-predict__noimg"></span>';
    return `<a class="es-predict__row es-predict__row--collection" href="${esc(c.u)}">
      <span class="es-predict__thumb">${img}</span>
      <span class="es-predict__text">
        <span class="es-predict__title">${esc(c.t)}</span>
        <span class="es-predict__vendor">قسم كامل · ${c.n} منتج</span>
      </span>
      <span class="es-predict__go">تصفّح القسم</span>
    </a>`;
  }

  function chipsHTML(heading) {
    return `<div class="es-predict__suggest">
      <p class="es-predict__hint">${esc(heading)}</p>
      <div class="es-predict__chips">
        ${CATEGORIES.map(
          ([label, tag]) =>
            `<a class="es-predict__chip" href="/collections/all/${encodeURIComponent(tag)}">${esc(label)}</a>`
        ).join('')}
      </div>
    </div>`;
  }

  function longestWord(q) {
    const parts = q.trim().split(/\s+/).filter((w) => w.length > 2);
    if (parts.length < 2) return null;
    return parts.sort((a, b) => b.length - a.length)[0];
  }

  function mount(component) {
    const wrapper = component.querySelector('.predictive-search-form__content-wrapper');
    const input = component.querySelector('input[name="q"]');
    if (!wrapper || !input || wrapper.querySelector('.es-predict')) return;

    const panel = document.createElement('div');
    panel.className = 'es-predict';
    panel.hidden = true;
    wrapper.insertBefore(panel, wrapper.firstChild);

    let timer = null;
    let seq = 0;
    const state = { q: '', page: 1, more: false, loading: false, shown: 0, total: 0 };

    const countText = () =>
      state.shown < state.total
        ? `${state.shown} من ${state.total} نتيجة`
        : `${state.total} نتيجة`;

    function shell(headline, body) {
      panel.innerHTML =
        `<p class="es-predict__count" data-count>${headline}</p>` +
        `<div class="es-predict__list" data-list>${body}</div>` +
        `<a class="es-predict__all" data-all href="/search?type=product&q=${encodeURIComponent(state.q)}"></a>`;
      panel.hidden = false;
      panel.scrollTop = 0;
      syncAll();
    }

    function syncAll() {
      const all = panel.querySelector('[data-all]');
      if (!all) return;
      all.textContent = `عرض كل النتائج (${state.total})`;
      all.href = `/search?type=product&q=${encodeURIComponent(state.q)}`;
      all.hidden = state.total <= state.shown;
    }

    async function loadMore() {
      if (state.loading || !state.more) return;
      state.loading = true;
      panel.classList.add('is-loading');
      try {
        const data = await query(state.q, state.page + 1);
        if (data.q !== state.q) return;
        state.page += 1;
        state.more = data.more;
        const kept = narrow(data.items, state.q);
        state.shown += kept.length;
        const list = panel.querySelector('[data-list]');
        if (list) list.insertAdjacentHTML('beforeend', kept.map(rowHTML).join(''));
        const c = panel.querySelector('[data-count]');
        if (c) c.textContent = countText();
        syncAll();
      } catch (e) {
        state.more = false;
      } finally {
        state.loading = false;
        panel.classList.remove('is-loading');
      }
    }

    panel.addEventListener('scroll', () => {
      if (panel.scrollTop + panel.clientHeight >= panel.scrollHeight - NEAR_END) loadMore();
    });

    async function run() {
      const q = input.value.trim();
      if (q.length < MIN) {
        panel.hidden = true;
        panel.innerHTML = '';
        return;
      }
      const mine = ++seq;
      panel.classList.add('is-loading');
      try {
        const asked = expandQuery(q);
        const data = await query(asked, 1);
        if (mine !== seq) return;

        if (data.n > 0) {
          const kept = narrow(data.items, q);
          const groups = collectionsFor(data.cols, q);
          Object.assign(state, { q: asked, page: 1, more: data.more, shown: kept.length, total: data.n });
          shell(countText(), groups.map(collectionHTML).join('') + kept.map(rowHTML).join(''));
          // Ten rows rarely fill the panel, and a list that cannot scroll never
          // asks for the next page. Prime it, but only a few times.
          for (let i = 0; i < MAX_AUTOFILL; i++) {
            if (!state.more || panel.scrollHeight > panel.clientHeight + NEAR_END) break;
            await loadMore();
            if (mine !== seq) return;
          }
          return;
        }

        const alt = longestWord(q);
        if (alt) {
          const near = await query(alt, 1);
          if (mine !== seq) return;
          if (near.n > 0) {
            Object.assign(state, { q: alt, page: 1, more: near.more, shown: near.items.length, total: near.n });
            shell(
              `مفيش نتيجة لـ «${esc(q)}» — دي أقرب حاجة لـ «${esc(alt)}»`,
              near.items.map(rowHTML).join('')
            );
            return;
          }
        }
        Object.assign(state, { q, page: 1, more: false, shown: 0, total: 0 });
        panel.innerHTML =
          `<p class="es-predict__count">مفيش نتيجة لـ «${esc(q)}»</p>` +
          chipsHTML('جرّب تدوّر في الأقسام دي:');
        panel.hidden = false;
      } catch (e) {
        panel.hidden = true;
      } finally {
        panel.classList.remove('is-loading');
      }
    }

    const form = component.querySelector('form');
    if (form) {
      form.addEventListener('submit', () => {
        input.value = expandQuery(input.value.trim());
      });
    }

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(run, WAIT);
    });
    input.addEventListener('search', run);
    if (input.value) run();
  }

  const boot = () => document.querySelectorAll('predictive-search-component').forEach(mount);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* Horizon re-renders the search section, so a new predictive-search-component
     can appear at any point and has to be mounted. What must not happen is what
     used to: `boot` — a document-wide querySelectorAll — running on every single
     mutation batch, undebounced, on a page whose body is mutated many hundreds
     of times while the recommendations, the pixels and the review widget land.
     Only an added element that is, or contains, one of these components is worth
     a pass, and even then one pass covers a burst of them. */
  let bootQueued = false;
  const scheduleBoot = () => {
    if (bootQueued) return;
    bootQueued = true;
    setTimeout(() => {
      bootQueued = false;
      boot();
    }, 200);
  };

  const bringsComponent = (node) =>
    node.nodeType === 1 &&
    (node.matches?.('predictive-search-component') || node.querySelector?.('predictive-search-component'));

  new MutationObserver((records) => {
    if (bootQueued) return;
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (bringsComponent(node)) return scheduleBoot();
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
